'use client';
import React, { useState, useMemo } from 'react';
import { Activity, Filter, ChevronRight, MapPin, Users, Radio, TrendingUp, ExternalLink, Target, Package, Send, Star, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { regionalEvents, categoryConfig, statusConfig } from './intentData';
import type { RegionalEvent, EventCategory, EventStatus } from './intentData';
import { useRouter } from 'next/navigation';

const ALL_CATEGORIES: EventCategory[] = ['retail', 'infrastructure', 'sports', 'education', 'entertainment', 'construction', 'government', 'healthcare', 'disaster'];
const ALL_STATUSES: EventStatus[] = ['live', 'upcoming', 'confirmed', 'rumored'];

// Inventory pool for random bundling
const ROADSIDE_POOL = [
  'Billboard #1 — Hwy 2 & 3rd St, Bemidji',
  'Billboard #2 — Hwy 71 South, Park Rapids',
  'Digital Screen — Sanford Center Entrance',
  'Billboard #3 — Hwy 2 East, Grand Rapids',
  'Digital Screen — Northland Mall, Thief River Falls',
  'Billboard #4 — Hwy 10 & Main St, Wadena',
  'Digital Screen — Walker Town Center',
  'Billboard #5 — Hwy 371 North, Brainerd',
  'Digital Screen — Bagley Community Center',
  'Billboard #6 — Hwy 2 West, Bemidji',
];

const TRANSIT_OOH_POOL = [
  'Transit OOH — Route 2 (Franklin Ave & Hennepin Ave → Stadium Village)',
  'Transit OOH — Route 3 (North Loop → Union Depot)',
  'Transit OOH — Route 4 (Silver Lake Rd → 82nd St Transit Center)',
  'Transit OOH — Route 5 (Brooklyn Center Transit Center → Chicago Ave)',
  'Transit OOH — Route 7 (Wirth Chalet → 66th St & Richfield Pkwy)',
  'Transit OOH — Route 9 (Louisiana Ave Transit Center → 46th St Station)',
  'Transit OOH — Route 10 (Northtown Transit Center → Minneapolis Convention Center)',
  'Transit OOH — Route 11 (Fremont Ave → 46th St Station)',
  'Transit OOH — Route 14 (Downtown Minneapolis → Uptown Transit Station)',
];

function getRandomInventories(count: number = 3): string[] {
  const shuffledRoadside = [...ROADSIDE_POOL].sort(() => Math.random() - 0.5);
  const shuffledTransit = [...TRANSIT_OOH_POOL].sort(() => Math.random() - 0.5);
  // Always include at least 1 transit OOH and fill the rest with roadside
  const transitCount = Math.max(1, Math.floor(count / 2));
  const roadsideCount = count - transitCount;
  return [
    ...shuffledRoadside.slice(0, roadsideCount),
    ...shuffledTransit.slice(0, transitCount),
  ].sort(() => Math.random() - 0.5);
}

function LiveTicker() {
  const tickers = [
    'TARGET FLAGSHIP · Bemidji · Opens May 12',
    'LUMBERJACKS PLAYOFFS · 4,500 fans/game · 6 home games',
    'HIGHWAY 71 EXPANSION · 8,400 diverted vehicles/day',
    'LAKELAND MUSIC FEST · 12,000 attendees · Jul 18-20',
    'WALMART GROUNDBREAKING · Park Rapids · Jun 3',
    'SANFORD CLINIC · Bagley · Opens Jul 15',
    'AMAZON DC RUMORED · Brainerd · 800+ jobs',
    '⚠️ SEVERE WEATHER ALERT · Northern MN · 180,000 households at risk',
  ];

  return (
    <div style={{ position: 'relative', overflow: 'hidden', background: '#171717', borderBottom: '1.5px solid #2A2A2A', height: '32px', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
      <div style={{ flexShrink: 0, padding: '0 12px', display: 'flex', alignItems: 'center', gap: '6px', borderRight: '1.5px solid #2A2A2A', height: '100%', background: '#5C00C4' }}>
        <Radio size={10} style={{ color: '#ffffff' }} className="animate-pulse" />
        <span style={{ fontSize: '10px', fontWeight: 900, color: '#ffffff', letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: "'Space Grotesk', sans-serif" }}>Live</span>
      </div>
      <div className="overflow-hidden flex-1">
        <div className="flex animate-[ticker_40s_linear_infinite] whitespace-nowrap">
          {[...tickers, ...tickers].map((t, i) => (
            <span key={i} style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', padding: '0 24px', flexShrink: 0, fontFamily: "'Rethink Sans', sans-serif" }}>
              <span style={{ color: 'rgba(255,255,255,0.9)', fontWeight: 700 }}>{t.split('·')[0]}</span>
              {t.includes('·') && <span style={{ color: '#BE0CF4', fontWeight: 600 }}>· {t.split('·').slice(1).join('·')}</span>}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, accentColor }: { label: string; value: string; sub: string; accentColor: string }) {
  return (
    <div style={{ background: '#FFFFFF', border: '1.5px solid rgba(0,0,0,.1)', borderRadius: '14px', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: '2px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
      <span style={{ fontSize: '10px', color: '#8A8A8A', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif" }}>{label}</span>
      <span style={{ fontSize: '26px', fontWeight: 900, lineHeight: 1, fontFamily: "'Space Grotesk', sans-serif", color: accentColor }}>{value}</span>
      <span style={{ fontSize: '11px', color: '#8A8A8A', fontFamily: "'Rethink Sans', sans-serif" }}>{sub}</span>
    </div>
  );
}

function EventRow({
  event,
  selected,
  onClick,
}: {
  event: RegionalEvent;
  selected: boolean;
  onClick: () => void;
}) {
  const cat = categoryConfig[event.category];
  const stat = statusConfig[event.status];

  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', textAlign: 'left', padding: '14px 16px',
        borderBottom: '1px solid rgba(0,0,0,.08)', transition: 'all 0.15s',
        background: selected ? 'rgba(92,0,196,0.06)' : 'transparent',
        borderLeft: selected ? '3px solid #5C00C4' : '3px solid transparent',
        display: 'block',
      }}
      onMouseEnter={e => { if (!selected) (e.currentTarget as HTMLElement).style.background = '#F7F7F8'; }}
      onMouseLeave={e => { if (!selected) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-1.5">
          <span className={`inline-block w-2 h-2 rounded-full ${stat.dot} ${event.status === 'live' ? 'animate-pulse' : ''}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span style={{ fontSize: '10px', fontWeight: 800, letterSpacing: '0.08em', color: '#8A8A8A' }}>{stat.label}</span>
            <span style={{ fontSize: '10px', padding: '1px 7px', borderRadius: '20px', fontWeight: 700, background: '#E6CCFF', color: '#5C00C4', border: '1px solid #C9A8F5' }}>
              {cat.label}
            </span>
          </div>
          <p style={{ fontSize: '13px', fontWeight: 700, lineHeight: 1.4, marginBottom: '4px', color: selected ? '#5C00C4' : '#000000' }}>
            {event.title}
          </p>
          <div className="flex items-center gap-3 text-xs" style={{ color: '#8A8A8A' }}>
            <span className="flex items-center gap-1">
              <MapPin size={10} />
              {event.location}
            </span>
            <span className="flex items-center gap-1">
              <Users size={10} />
              {event.footfallEstimate}
            </span>
          </div>
        </div>

        <ChevronRight
          size={14}
          style={{ flexShrink: 0, marginTop: '4px', color: selected ? '#5C00C4' : 'rgba(0,0,0,.2)', transform: selected ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}
        />
      </div>
    </button>
  );
}

function BrandTag({ brand }: { brand: { name: string; logo: string; vertical: string; fitScore: number; reason: string } }) {
  const scoreColor =
    brand.fitScore >= 90 ? '#5C00C4' : brand.fitScore >= 80 ? '#FEB34B' : '#6B7280';
  const scoreBg =
    brand.fitScore >= 90 ? '#F0E6FF' : brand.fitScore >= 80 ? 'transparent' : 'transparent';
  const scoreBorder =
    brand.fitScore >= 90 ? '#C9A8F5' : brand.fitScore >= 80 ? '#FEB34B' : '#9CA3AF';

  return (
    <div style={{ background: '#FFFFFF', border: '1.5px solid rgba(0,0,0,.1)', borderRadius: '12px', padding: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
      <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(92,0,196,0.08)', border: '1.5px solid rgba(92,0,196,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, color: '#5C00C4', flexShrink: 0 }}>
        {brand.logo}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <span style={{ fontSize: '12px', fontWeight: 700, color: '#000000' }} className="truncate">{brand.name}</span>
          <span style={{ fontSize: '11px', fontWeight: 800, color: scoreColor, background: scoreBg, border: `1.5px solid ${scoreBorder}`, padding: '1px 8px', borderRadius: '99px', flexShrink: 0 }}>{brand.fitScore}</span>
        </div>
        <span style={{ fontSize: '11px', color: '#8A8A8A', display: 'block', marginBottom: '4px' }}>{brand.vertical}</span>
        <p style={{ fontSize: '11px', color: '#575757', lineHeight: 1.6 }}>{brand.reason}</p>
      </div>
    </div>
  );
}

interface VerticalActionsProps {
  vertical: string;
  event: RegionalEvent;
  onBundle: (vertical: string, inventories: string[]) => void;
  onReachout: (vertical: string, bundledInventories?: string[]) => void;
}

function VerticalActions({ vertical, event, onBundle, onReachout }: VerticalActionsProps) {
  const [bundled, setBundled] = useState<string[] | null>(null);
  const [showBundle, setShowBundle] = useState(false);

  const handleBundle = () => {
    const inventories = getRandomInventories(3);
    setBundled(inventories);
    setShowBundle(true);
    onBundle(vertical, inventories);
  };

  return (
    <div className="mt-2">
      <div className="flex items-center gap-2">
        <button
          onClick={handleBundle}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', padding: '6px 12px', borderRadius: '8px', background: 'rgba(92,0,196,0.08)', color: '#5C00C4', border: '1.5px solid rgba(92,0,196,0.2)', cursor: 'pointer', fontWeight: 700 }}
        >
          <Package size={11} />
          Bundle
        </button>
        <button
          onClick={() => onReachout(vertical, bundled ?? undefined)}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', padding: '6px 12px', borderRadius: '8px', background: '#FFFFFF', color: '#575757', border: '1.5px solid rgba(0,0,0,.1)', cursor: 'pointer', fontWeight: 600 }}
        >
          <Send size={11} />
          Reachout
        </button>
      </div>
      {showBundle && bundled && (
        <div style={{ marginTop: '8px', padding: '10px 12px', borderRadius: '10px', background: '#F7F7F8', border: '1.5px solid rgba(0,0,0,.1)' }}>
          <p style={{ fontSize: '11px', fontWeight: 800, color: '#000000', marginBottom: '6px' }}>Bundled Inventories</p>
          <ul className="space-y-1">
            {bundled.map((inv, i) => (
              <li key={i} style={{ fontSize: '11px', color: '#575757', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#5C00C4', flexShrink: 0, display: 'inline-block' }} />
                {inv}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function formatVertical(v: string): string {
  const lower = v.toLowerCase();
  if (lower === 'qsr' || lower === 'quick service' || lower === 'fast food') return 'QSR';
  if (lower === 'homereconstruction' || lower === 'home reconstruction') return 'Home Reconstruction';
  return v.charAt(0).toUpperCase() + v.slice(1);
}

function EventDetail({ event, onClose, liveCount }: { event: RegionalEvent; onClose: () => void; liveCount: number }) {
  const cat = categoryConfig[event.category];
  const stat = statusConfig[event.status];
  const router = useRouter();

  const handleBundle = (vertical: string, inventories: string[]) => {
    console.log('Bundled for', vertical, inventories);
  };

  const handleReachout = (vertical: string, bundledInventories?: string[]) => {
    const params = new URLSearchParams({
      vertical: vertical,
      eventId: event.id,
      eventTitle: event.title,
      eventDate: event.date,
      eventLocation: event.location,
      eventDescription: event.description,
      eventCategory: event.category,
    });

    // If there are bundled inventories, detect transit vs roadside and pass bundle context
    if (bundledInventories && bundledInventories.length > 0) {
      const transitItems = bundledInventories.filter(inv => inv.toLowerCase().includes('transit ooh'));
      const roadsideItems = bundledInventories.filter(inv => !inv.toLowerCase().includes('transit ooh'));

      if (transitItems.length > 0 && roadsideItems.length > 0) {
        // Mixed bundle — pass full bundle context
        params.set('isMixedBundle', 'true');
        params.set('bundleTransitNames', transitItems.join(', '));
        params.set('bundleRoadsideNames', roadsideItems.join(', '));
        params.set('bundleInventories', bundledInventories.join('||'));
      } else if (transitItems.length > 0) {
        // Transit-only bundle
        params.set('bundleTransitNames', transitItems.join(', '));
        params.set('bundleInventories', bundledInventories.join('||'));
      } else {
        // Roadside-only bundle
        params.set('bundleRoadsideNames', roadsideItems.join(', '));
        params.set('bundleInventories', bundledInventories.join('||'));
      }
    }

    router.push(`/brand-selection-screen?${params.toString()}`);
  };

  return (
    <div className="flex flex-col h-full">
      {/* ── CAMPAIGN CARD: Event detail header ── */}
      <div style={{ padding: '16px 20px', borderBottom: '1.5px solid rgba(0,0,0,.1)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexShrink: 0, background: '#FFFFFF', position: 'relative' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #5C00C4, #BE0CF4, #5C00C4)' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#5C00C4', border: '1.5px solid rgba(92,0,196,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(92,0,196,0.2)' }}>
            <Activity size={18} style={{ color: '#FFFFFF' }} />
          </div>
          <div>
            <h2 style={{ fontSize: '15px', fontWeight: 800, color: '#000000', lineHeight: 1.3, letterSpacing: '-0.03em', fontFamily: "'Space Grotesk', sans-serif" }}>{event.title}</h2>
            <p style={{ fontSize: '11px', color: '#8A8A8A', marginTop: '2px', fontFamily: "'Rethink Sans', sans-serif" }}>{event.date} · {event.location}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#16a34a', fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif" }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} className="animate-pulse" />
            {liveCount} Live Events
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide">
        {/* Stats row — stat card style */}
        <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', borderBottom: '1.5px solid rgba(0,0,0,.1)', background: '#F7F7F8' }}>
          <div style={{ background: '#FFFFFF', border: '1.5px solid rgba(0,0,0,.1)', borderRadius: '12px', padding: '10px 14px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <p style={{ fontSize: '10px', color: '#8A8A8A', marginBottom: '2px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: "'Space Grotesk', sans-serif" }}>Footfall</p>
            <p style={{ fontSize: '15px', fontWeight: 900, color: '#5C00C4', fontFamily: "'Space Grotesk', sans-serif" }}>{event.footfallEstimate}</p>
          </div>
          <div style={{ background: '#FFFFFF', border: '1.5px solid rgba(0,0,0,.1)', borderRadius: '12px', padding: '10px 14px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <p style={{ fontSize: '10px', color: '#8A8A8A', marginBottom: '2px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: "'Space Grotesk', sans-serif" }}>Impact Radius</p>
            <p style={{ fontSize: '15px', fontWeight: 900, color: '#5C00C4', fontFamily: "'Space Grotesk', sans-serif" }}>{event.impactRadius}</p>
          </div>
          <div style={{ background: '#FFFFFF', border: '1.5px solid rgba(0,0,0,.1)', borderRadius: '12px', padding: '10px 14px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <p style={{ fontSize: '10px', color: '#8A8A8A', marginBottom: '2px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: "'Space Grotesk', sans-serif" }}>Region</p>
            <p style={{ fontSize: '15px', fontWeight: 900, color: '#000000', fontFamily: "'Space Grotesk', sans-serif", display: 'block', marginBottom: '0' }} className="truncate">{event.region}</p>
          </div>
        </div>

        {/* Description */}
        <div style={{ padding: '16px 20px', borderBottom: '1.5px solid rgba(0,0,0,.1)' }}>
          <p style={{ fontSize: '12px', color: '#575757', lineHeight: 1.7, fontFamily: "'Rethink Sans', sans-serif" }}>{event.description}</p>
          <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#8A8A8A', fontFamily: "'Rethink Sans', sans-serif" }}>
            <ExternalLink size={10} />
            <span>Source: {event.source}</span>
          </div>
        </div>

        {/* Mapped Verticals with CTAs */}
        <div style={{ padding: '16px 20px', borderBottom: '1.5px solid rgba(0,0,0,.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <Target size={13} style={{ color: '#5C00C4' }} />
            <span style={{ fontSize: '10px', fontWeight: 800, color: '#000000', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'Space Grotesk', sans-serif" }}>Target Verticals</span>
          </div>
          <div className="space-y-3">
            {event.mappedVerticals.map(v => (
              <div key={v} style={{ borderRadius: '10px', border: '1.5px solid rgba(0,0,0,.1)', background: '#FFFFFF', padding: '10px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: '#000000', fontFamily: "'Space Grotesk', sans-serif" }}>{formatVertical(v)}</span>
                <VerticalActions
                  vertical={v}
                  event={event}
                  onBundle={handleBundle}
                  onReachout={handleReachout}
                />
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
// ── Marquee Event Banner ─────────────────────────────────────────────────────
function MarqueeEventBanner() {
  const router = useRouter();
  const [collapsed, setCollapsed] = useState(true);

  const marqueeEvent: RegionalEvent = {
    id: 'marquee-lakeland',
    title: '🎵 Lakeland Music Festival',
    category: 'entertainment',
    status: 'upcoming',
    date: 'Jul 18–20',
    location: 'Northern Minnesota',
    footfallEstimate: '12,000',
    impactRadius: '150 miles',
    region: 'Northern MN',
    description: 'Annual 3-day outdoor concert festival drawing 12,000 attendees from across Minnesota and neighboring states.',
    source: 'Regional Events Intelligence',
    mappedVerticals: ['entertainment', 'food & beverage', 'retail', 'hospitality'],
    mappedBrands: [],
  };

  const handleBundle = (vertical: string, inventories: string[]) => {
    console.log('Marquee bundled for', vertical, inventories);
  };

  const handleReachout = (vertical: string, bundledInventories?: string[]) => {
    const params = new URLSearchParams({
      vertical,
      eventId: marqueeEvent.id,
      eventTitle: marqueeEvent.title,
      eventDate: marqueeEvent.date,
      eventLocation: marqueeEvent.location,
      eventDescription: marqueeEvent.description,
      eventCategory: marqueeEvent.category,
    });
    if (bundledInventories && bundledInventories.length > 0) {
      const transitItems = bundledInventories.filter(inv => inv.toLowerCase().includes('transit ooh'));
      const roadsideItems = bundledInventories.filter(inv => !inv.toLowerCase().includes('transit ooh'));
      if (transitItems.length > 0 && roadsideItems.length > 0) {
        params.set('isMixedBundle', 'true');
        params.set('bundleTransitNames', transitItems.join(', '));
        params.set('bundleRoadsideNames', roadsideItems.join(', '));
        params.set('bundleInventories', bundledInventories.join('||'));
      } else if (transitItems.length > 0) {
        params.set('bundleTransitNames', transitItems.join(', '));
        params.set('bundleInventories', bundledInventories.join('||'));
      } else {
        params.set('bundleRoadsideNames', roadsideItems.join(', '));
        params.set('bundleInventories', bundledInventories.join('||'));
      }
    }
    router.push(`/brand-selection-screen?${params.toString()}`);
  };

  return (
    <div style={{ margin: '16px 24px 4px', borderRadius: '12px', overflow: 'hidden', border: '1.5px solid rgba(254,179,75,0.4)', background: 'rgba(254,179,75,0.06)', flexShrink: 0 }}>
      {/* Collapsible header row */}
      <button
        onClick={() => setCollapsed(c => !c)}
        style={{ width: '100%', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: '12px', background: 'transparent', border: 'none', cursor: 'pointer' }}
      >
        <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'rgba(254,179,75,0.15)', border: '1.5px solid rgba(254,179,75,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Star size={14} style={{ color: '#FEB34B' }} />
        </div>
        <span style={{ fontSize: '10px', fontWeight: 900, letterSpacing: '0.1em', color: '#FEB34B', textTransform: 'uppercase', flex: 1, textAlign: 'left', fontFamily: "'Space Grotesk', sans-serif" }}>Marquee Event</span>
        <div className="flex items-center gap-2">
          <span style={{ fontSize: '12px', fontWeight: 700, color: '#171717', fontFamily: "'Space Grotesk', sans-serif" }}>🎵 Lakeland Music Festival</span>
          <span style={{ fontSize: '11px', color: '#8A8A8A', fontFamily: "'Rethink Sans', sans-serif" }}>· Jul 18–20 · 12,000 attendees</span>
        </div>
        {collapsed ? (
          <ChevronDown size={14} style={{ color: '#FEB34B', flexShrink: 0, marginLeft: '8px' }} />
        ) : (
          <ChevronUp size={14} style={{ color: '#FEB34B', flexShrink: 0, marginLeft: '8px' }} />
        )}
      </button>

      {/* Expandable body */}
      {!collapsed && (
        <div style={{ padding: '0 20px 16px', display: 'flex', alignItems: 'flex-start', gap: '16px', borderTop: '1px solid rgba(254,179,75,0.3)' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(254,179,75,0.15)', border: '1.5px solid rgba(254,179,75,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '12px' }}>
            <Star size={18} style={{ color: '#FEB34B' }} />
          </div>

          <div className="flex-1 min-w-0 pt-3">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '20px', background: 'rgba(254,179,75,0.15)', color: '#FEB34B', border: '1px solid rgba(254,179,75,0.4)', fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif" }}>Entertainment</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#16a34a', fontWeight: 600 }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} className="animate-pulse" />
                Upcoming
              </span>
            </div>
            <h3 style={{ fontSize: '14px', fontWeight: 800, color: '#171717', lineHeight: 1.3, marginBottom: '4px', letterSpacing: '-0.02em', fontFamily: "'Space Grotesk', sans-serif" }}>
              🎵 Lakeland Music Festival
            </h3>
            <p style={{ fontSize: '12px', color: '#575757', lineHeight: 1.7, marginBottom: '10px', fontFamily: "'Rethink Sans', sans-serif" }}>
              Annual 3-day outdoor concert festival drawing 12,000 attendees from across Minnesota and neighboring states. Highest single-event OOH window in Northern MN — 150-mile audience draw radius.
            </p>
            <div className="flex items-center gap-4 flex-wrap mb-3">
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#8A8A8A', fontFamily: "'Rethink Sans', sans-serif" }}>
                <MapPin size={11} style={{ color: '#FEB34B' }} />
                Northern Minnesota
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#8A8A8A', fontFamily: "'Rethink Sans', sans-serif" }}>
                <Users size={11} style={{ color: '#FEB34B' }} />
                12,000 over 3 days
              </span>
              <span style={{ fontSize: '11px', color: '#8A8A8A', fontFamily: "'Rethink Sans', sans-serif" }}>📅 Jul 18–20</span>
              <span style={{ fontSize: '11px', color: '#8A8A8A', fontFamily: "'Rethink Sans', sans-serif" }}>📡 150-mile impact radius</span>
            </div>

            <div style={{ borderTop: '1px solid rgba(254,179,75,0.3)', paddingTop: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <Target size={11} style={{ color: '#FEB34B' }} />
                <span style={{ fontSize: '10px', fontWeight: 800, color: '#FEB34B', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'Space Grotesk', sans-serif" }}>Target Verticals</span>
              </div>
              <div className="space-y-2">
                {marqueeEvent.mappedVerticals.map(v => (
                  <div key={v} style={{ borderRadius: '8px', border: '1px solid rgba(254,179,75,0.25)', background: 'rgba(254,179,75,0.05)', padding: '8px 12px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#171717', fontFamily: "'Space Grotesk', sans-serif" }}>{formatVertical(v)}</span>
                    <VerticalActions
                      vertical={v}
                      event={marqueeEvent}
                      onBundle={handleBundle}
                      onReachout={handleReachout}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ flexShrink: 0, textAlign: 'center', paddingTop: '12px' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '10px', background: 'rgba(254,179,75,0.15)', border: '1.5px solid rgba(254,179,75,0.4)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: '18px', fontWeight: 900, color: '#FEB34B', lineHeight: 1, fontFamily: "'Space Grotesk', sans-serif" }}>98</span>
              <span style={{ fontSize: '9px', color: '#FEB34B', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: "'Space Grotesk', sans-serif" }}>score</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function IntentContent() {
  const [selectedEvent, setSelectedEvent] = useState<RegionalEvent | null>(regionalEvents[0]);
  const [activeCategories, setActiveCategories] = useState<Set<EventCategory>>(new Set());
  const [activeStatuses, setActiveStatuses] = useState<Set<EventStatus>>(new Set());
  const [search, setSearch] = useState('');

  const toggleCategory = (cat: EventCategory) => {
    setActiveCategories(prev => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  };

  const toggleStatus = (s: EventStatus) => {
    setActiveStatuses(prev => {
      const next = new Set(prev);
      next.has(s) ? next.delete(s) : next.add(s);
      return next;
    });
  };

  const filtered = useMemo(() => {
    return regionalEvents.filter(e => {
      if (activeCategories.size > 0 && !activeCategories.has(e.category)) return false;
      if (activeStatuses.size > 0 && !activeStatuses.has(e.status)) return false;
      if (search && !e.title.toLowerCase().includes(search.toLowerCase()) && !e.location.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [activeCategories, activeStatuses, search]);

  const liveCount = regionalEvents.filter(e => e.status === 'live').length;
  const upcomingCount = regionalEvents.filter(e => e.status === 'upcoming' || e.status === 'confirmed').length;
  const totalBrands = regionalEvents.reduce((acc, e) => acc + e.mappedBrands.length, 0);

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ background: '#F7F7F8' }}>
      {/* Ticker */}
      <LiveTicker />

      {/* ── Page Header ── */}
      <div style={{ padding: '16px 24px', borderBottom: '1.5px solid rgba(0,0,0,.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexShrink: 0, background: '#FFFFFF', position: 'relative' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#5C00C4' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#5C00C4', border: '1.5px solid rgba(92,0,196,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(92,0,196,0.2)' }}>
            <Activity size={18} style={{ color: '#FFFFFF' }} />
          </div>
          <div>
            <h1 style={{ fontSize: '16px', fontWeight: 900, color: '#171717', letterSpacing: '-0.03em', fontFamily: "'Space Grotesk', sans-serif" }}>Signals Monitor</h1>
            <p style={{ fontSize: '11px', color: '#8A8A8A', fontFamily: "'Rethink Sans', sans-serif" }}>Regional event intelligence · Northern Minnesota</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#16a34a', fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif" }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} className="animate-pulse" />
            {liveCount} Live Events
          </span>
        </div>
      </div>

      {/* ── STAT CARDS BAR ── */}
      <div style={{ padding: '12px 24px', borderBottom: '1.5px solid rgba(0,0,0,.1)', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', flexShrink: 0, background: '#F7F7F8' }}>
        <StatCard label="Total Events" value={String(regionalEvents.length)} sub="tracked in region" accentColor="#171717" />
        <StatCard label="Live Now" value={String(liveCount)} sub="active events" accentColor="#16a34a" />
        <StatCard label="Upcoming" value={String(upcomingCount)} sub="confirmed + upcoming" accentColor="#FEB34B" />
        <StatCard label="Brand Signals" value={String(totalBrands)} sub="mapped opportunities" accentColor="#5C00C4" />
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel */}
        <div style={{ width: '380px', flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: '1.5px solid rgba(0,0,0,.1)', overflow: 'hidden', background: '#FFFFFF' }}>
          {/* Filters */}
          <div style={{ padding: '12px 16px', borderBottom: '1.5px solid rgba(0,0,0,.1)', flexShrink: 0 }} className="space-y-2.5">
            <div style={{ position: 'relative' }}>
              <Search size={13} style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', color: '#8A8A8A', pointerEvents: 'none' }} />
              <input
                type="text"
                placeholder="Search events or locations..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{
                  width: '100%', background: '#FFFFFF',
                  border: '1.5px solid rgba(0,0,0,.15)',
                  borderRadius: '10px',
                  padding: '8px 12px 8px 32px',
                  fontSize: '12px', color: '#171717', outline: 'none',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                  boxSizing: 'border-box',
                  fontFamily: "'Rethink Sans', sans-serif",
                }}
                onFocus={e => {
                  (e.target as HTMLInputElement).style.borderColor = '#5C00C4';
                  (e.target as HTMLInputElement).style.boxShadow = '0 0 0 3px rgba(92,0,196,0.1)';
                }}
                onBlur={e => {
                  (e.target as HTMLInputElement).style.borderColor = 'rgba(0,0,0,.15)';
                  (e.target as HTMLInputElement).style.boxShadow = 'none';
                }}
              />
            </div>

            <div className="flex items-center gap-1.5 flex-wrap">
              <Filter size={11} style={{ color: '#8A8A8A', flexShrink: 0 }} />
              {ALL_CATEGORIES.map(cat => {
                const c = categoryConfig[cat];
                const active = activeCategories.has(cat);
                return (
                  <button
                    key={cat}
                    onClick={() => toggleCategory(cat)}
                    style={{
                      fontSize: '10px', padding: '2px 8px', borderRadius: '20px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s',
                      background: active ? '#E6CCFF' : '#F7F7F8',
                      color: active ? '#5C00C4' : '#575757',
                      border: active ? '1.5px solid #C9A8F5' : '1.5px solid rgba(0,0,0,.1)',
                      fontFamily: "'Space Grotesk', sans-serif",
                    }}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-1.5 flex-wrap">
              {ALL_STATUSES.map(s => {
                const sc = statusConfig[s];
                const active = activeStatuses.has(s);
                return (
                  <button
                    key={s}
                    onClick={() => toggleStatus(s)}
                    style={{
                      fontSize: '10px', padding: '2px 8px', borderRadius: '20px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: '4px',
                      background: active ? '#E6CCFF' : '#F7F7F8',
                      color: active ? '#5C00C4' : '#575757',
                      border: active ? '1.5px solid #C9A8F5' : '1.5px solid rgba(0,0,0,.1)',
                      fontFamily: "'Space Grotesk', sans-serif",
                    }}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                    {sc.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Event list */}
          <div className="flex-1 overflow-y-auto scrollbar-hide">
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center px-6">
                <TrendingUp size={32} style={{ color: 'rgba(0,0,0,.1)', marginBottom: '12px' }} />
                <p style={{ fontSize: '13px', fontWeight: 600, color: '#575757', fontFamily: "'Space Grotesk', sans-serif" }}>No events match filters</p>
                <p style={{ fontSize: '11px', color: '#8A8A8A', marginTop: '4px', fontFamily: "'Rethink Sans', sans-serif" }}>Try adjusting your filters</p>
              </div>
            ) : (
              filtered.map(event => (
                <EventRow
                  key={event.id}
                  event={event}
                  selected={selectedEvent?.id === event.id}
                  onClick={() => setSelectedEvent(event)}
                />
              ))
            )}
          </div>

          <div style={{ padding: '10px 16px', borderTop: '1.5px solid rgba(0,0,0,.1)', flexShrink: 0 }}>
            <p style={{ fontSize: '11px', color: '#8A8A8A', fontFamily: "'Rethink Sans', sans-serif" }}>{filtered.length} of {regionalEvents.length} events</p>
          </div>
        </div>

        {/* Right Panel */}
        <div className="flex-1 overflow-y-auto" style={{ background: '#F7F7F8' }}>
          <MarqueeEventBanner />

          <div style={{ marginTop: '12px', minHeight: '400px' }}>
            {selectedEvent ? (
              <EventDetail
                event={selectedEvent}
                onClose={() => setSelectedEvent(null)}
                liveCount={liveCount}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-center px-8">
                <div style={{ background: '#171717', borderRadius: '20px', padding: '32px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
                  <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: '#5C00C4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Activity size={24} style={{ color: '#FFFFFF' }} />
                  </div>
                  <p style={{ fontSize: '15px', fontWeight: 700, color: '#FFFFFF', marginBottom: '0', fontFamily: "'Space Grotesk', sans-serif" }}>Select an event</p>
                  <p style={{ fontSize: '13px', color: '#8A8A8A', maxWidth: '280px', lineHeight: 1.6, margin: 0, fontFamily: "'Rethink Sans', sans-serif" }}>
                    Click any regional event to see mapped verticals, brand targets, and launch strategies.
                  </p>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: '#FEB34B', letterSpacing: '0.02em', fontFamily: "'Space Grotesk', sans-serif" }}>← Select from list</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
